import React, { useState } from "react";
import { Link, useParams, withRouter } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { announceEventComplete } from "../../../redux/actions/events";
import { useAnnounceApp } from "../../../components/events/publishAppModal";
import { isEventPublished } from "../../../helpers/eventPublication";
import { RevButton, RevCard, RevChip, RevEmpty, RevTipRow } from "../../../components/ds";
import PhotoUploader from "./photoUploader";

/**
 * Subir fotos: solo fotos normales del evento (dueño y colaborador). Activar los selfies cierra
 * la subida; lo que va a la galería pública se sube desde su pestaña, que sigue abierta siempre.
 */
const EventPhotos = (props) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [isSharedEvent] = useState(window.location.pathname.includes('/shared-photo'));
  // Lo que la tarjeta de subida sabe del evento y la columna lateral pinta.
  const [uploader, setUploader] = useState({ loading: true, busy: true, total: 0 });
  const photoControlsLocked = uploader.busy;
  const loading = uploader.loading;
  const totalEventPhotos = uploader.total;

  // Older events may have completed=true by default without ever being published.
  const eventCompleted = isEventPublished(props.event);
  const lastNoticeDate = props.event?.lastNoticeDate || props.event?.completedDate;

  // Activar los selfies cierra la subida para siempre. Nos guiamos por la fecha y no por
  // `completed`: los eventos anteriores a esta función llegan con `completed: true` por
  // defecto desde el backend y quedarían bloqueados sin haberse publicado nunca.
  const uploadsLocked = eventCompleted;
  // En la pestaña de colaborador el evento llega en una segunda petición: hasta que esté,
  // no sabemos si la galería ya se publicó y no se enseña el formulario de subida.
  const sharedEventPending = isSharedEvent && !props.event;

  const formatNoticeDate = (value) => (value ? new Date(value).toLocaleString(i18n.language) : "");

  // «Volver a avisar» reenvía el aviso a quien entró con el código (una vez cada 24 h).
  // «Terminar subida y activar selfies» vive en la cabecera del evento; aquí solo se informa del estado.
  const { announcing, announce: notifyAgain } = useAnnounceApp({
    eventId: id,
    code: props.event?.code,
    announce: props.announceEventComplete,
    formatDate: formatNoticeDate,
  });

  // ======== Render ========
  return (
    <div>
      <div className="rev-cols rev-cols--flush">
        <div className="rev-cols__main">
          <PhotoUploader
            eventId={id}
            user={props.user}
            title={t("eventPhotos.upload.title")}
            locked={uploadsLocked}
            pending={sharedEventPending}
            onStatus={setUploader}
            lockedContent={
              <RevEmpty
                icon="lock"
                title={t("eventPhotos.upload.locked.title")}
                body={t("eventPhotos.upload.locked.body", { date: formatNoticeDate(props.event?.completedDate) })}
              >
                {/* Solo el dueño sube a la galería pública. */}
                {!isSharedEvent && (
                  <span className="rev-empty__body">
                    <Trans i18nKey="eventPhotos.upload.locked.publicHint"
                      components={[<Link to={`/events/${id}/public-gallery`} key="0" />]} />
                  </span>
                )}
              </RevEmpty>
            }
          />
        </div>

        <div className="rev-cols__aside">
          {!isSharedEvent && props.event && (
            <RevCard title={t("eventPhotos.appPublication.title")}>
              {loading ? (
                <RevChip>—</RevChip>
              ) : eventCompleted ? (
                <RevChip tone="ok">{t("eventPhotos.appPublication.statusDone")}</RevChip>
              ) : totalEventPhotos > 0 ? (
                <RevChip tone="warn">{t("eventPhotos.appPublication.statusPending")}</RevChip>
              ) : (
                <RevChip>{t("eventPhotos.appPublication.statusNone")}</RevChip>
              )}
              <span className="rev-help">
                {!eventCompleted
                  ? t("eventPhotos.appPublication.body")
                  : lastNoticeDate
                    ? t("eventPhotos.appPublication.bodyDone", { date: formatNoticeDate(lastNoticeDate) })
                    : t("eventPhotos.appPublication.bodyDoneNoDate")}
              </span>
              {eventCompleted && (
                <RevButton variant="secondary" size="sm" loading={announcing}
                  disabled={photoControlsLocked || announcing} onClick={notifyAgain}>
                  {t("eventPhotos.completeGallery.ctaAgain")}
                </RevButton>
              )}
              <span className="rev-help rev-help--divider">
                {t("eventPhotos.completeGallery.note", { code: props.event?.code })}
                {!uploadsLocked && (
                  <>
                    {" "}
                    <Trans
                      i18nKey="eventPhotos.completeGallery.noteCode"
                      components={[<Link to={`/events/${id}/basic`} key="0" />]}
                    />
                  </>
                )}
              </span>
            </RevCard>
          )}

          <RevCard title={t("eventPhotos.tips.title")}>
            <RevTipRow n="01" title={t("eventPhotos.tips.tip1")} />
            <RevTipRow n="02" title={t("eventPhotos.tips.tip2")} />
            <RevTipRow n="03" title={t("eventPhotos.tips.tip3")} />
          </RevCard>
        </div>
      </div>

    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  user: state.usersReducer.user,
  // Los eventos propios llegan por GET /event; los compartidos, por
  // /get-event-shared-with-me a otro slice. El colaborador necesita el evento para
  // saber si la galería ya se publicó y la subida está cerrada.
  event:
    (state.eventsReducer.events || []).find((e) => e.id === ownProps.eventId) ||
    (state.eventsReducer.eventSharedWithMe || []).find(
      (item) => item.revopicEvent?.id === ownProps.eventId
    )?.revopicEvent,
});

const mapDispatchToProps = {
  announceEventComplete,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventPhotos));
